import {
  ZendeskChatDepartment,
  ZendeskDepartments,
  ZendeskSettings,
  ZendeskStatus,
  ZendeskUserDetails,
  ZendeskWebWidgetAction,
  ZendeskWidgetUserEvent
} from '../../../types/zendesk.d';
import { zendeskWidgetConfig } from './zendeskWidgetConfig';

/** both `window.zE` & `window.zEACLoaded` are fundamental in ensuring widget functionality */

export const isZendeskWidgetLoaded = (): boolean =>
  window?.zEACLoaded === true && window?.zE != null;

/** widget appearance toggle */

export const closeZendeskWidget = (): void => {
  if (isZendeskWidgetLoaded()) {
    window.zE('webWidget', 'close');
  }
};

export const hideZendeskWidget = (): void => {
  if (isZendeskWidgetLoaded()) {
    window.zE('webWidget', 'hide');
  }
};

export const showZendeskWidget = (): void => {
  if (isZendeskWidgetLoaded()) {
    window.zE('webWidget', 'show');
  }
};

export const toggleZendeskWidget = (): void => {
  if (isZendeskWidgetLoaded()) {
    window.zE('webWidget', 'toggle');
  }
};

/** widget configuration */

export const resetZendeskWidget = (): void => {
  if (isZendeskWidgetLoaded()) {
    window.zE('webWidget', 'reset');
  }
};

export const setZendeskWidgetDefaultLocale = (): void => {
  if (isZendeskWidgetLoaded()) {
    window.zE('webWidget', 'setLocale', zendeskWidgetConfig.defaultLocale);
  }
};

export const updateZendeskWidgetPath = (url?: string): void => {
  if (isZendeskWidgetLoaded()) {
    if (url != null) window.zE('webWidget', 'updatePath', { url });
    else window.zE('webWidget', 'updatePath');
  }
};

export const updateZendeskWidgetSettings = (settings: ZendeskSettings): void => {
  if (settings != null) {
    window.zESettings = settings;

    if (isZendeskWidgetLoaded()) {
      window.zE('webWidget', 'updateSettings', settings);
    }
  }
};

/** end-user session */

export const clearZendeskUserSession = (): void => {
  if (isZendeskWidgetLoaded()) {
    window.zE('webWidget', 'logout');
  }
};

export const identifyZendeskUser = (userDetails: ZendeskUserDetails) => {
  if (isZendeskWidgetLoaded() && userDetails != null) {
    window.zE('webWidget', 'identify', userDetails);
  }
};

export const prefillZendeskUserDetails = (userDetails: ZendeskUserDetails) => {
  if (isZendeskWidgetLoaded() && userDetails != null) {
    window.zE('webWidget', 'prefill', userDetails);
  }
};

/** end-user events */

export const onZendeskWidgetClose = (callback: () => void): void => {
  if (isZendeskWidgetLoaded() && callback != null && typeof callback === 'function') {
    window.zE('webWidget:on', 'close', callback);
  }
};

export const onZendeskWidgetOpen = (callback: () => void): void => {
  if (isZendeskWidgetLoaded() && callback != null && typeof callback === 'function') {
    window.zE('webWidget:on', 'open', callback);
  }
};

/** chat events */

export const onZendesWidgetUserEvent = (
  callback: (userEvent: ZendeskWidgetUserEvent) => void
): void => {
  if (isZendeskWidgetLoaded() && callback != null && typeof callback === 'function') {
    window.zE('webWidget:on', 'userEvent', callback);
  }
};

export const onZendeskWidgetEventOpened = (callback: () => void): void => {
  if (callback != null && typeof callback === 'function') {
    onZendesWidgetUserEvent((userEvent: ZendeskWidgetUserEvent) => {
      if (userEvent.action === ZendeskWebWidgetAction.Opened) {
        callback();
      }
    });
  }
};

export const onZendeskWidgetEventMinimised = (callback: () => void): void => {
  if (callback != null && typeof callback === 'function') {
    onZendesWidgetUserEvent((userEvent: ZendeskWidgetUserEvent) => {
      if (userEvent.action === ZendeskWebWidgetAction.Minimised) {
        callback();
      }
    });
  }
};

/** chat department info */

export const getZendeskChatDepartment = (
  name: ZendeskDepartments
): ZendeskChatDepartment | undefined => {
  let department: ZendeskChatDepartment | undefined;

  if (isZendeskWidgetLoaded()) {
    department = window.zE('webWidget:get', 'chat:department', name) as ZendeskChatDepartment;
  }

  return department;
};

export const getZendeskChatDepartments = (): ZendeskChatDepartment[] => {
  let departments: ZendeskChatDepartment[] = [];

  if (isZendeskWidgetLoaded()) {
    departments = window.zE('webWidget:get', 'chat:departments') as ZendeskChatDepartment[];
  }

  return departments;
};

export const isChatDepartmentOnline = (name: ZendeskDepartments): boolean => {
  const department = getZendeskChatDepartment(name);

  if (department != null) {
    return department.status === ZendeskStatus.Online;
  }

  return false;
};

export const isCustomerCareChatDepartmentOnline = (): boolean =>
  isChatDepartmentOnline(ZendeskDepartments.CustomerCare);
export const isPCAsChatDepartmentOnline = (): boolean =>
  isChatDepartmentOnline(ZendeskDepartments.PrivateClientAdvisors);

/** chat department status events */

export const onZendeskChatDepartmentStatusChange = (
  callback: (department: ZendeskChatDepartment) => void
): void => {
  if (isZendeskWidgetLoaded() && callback != null && typeof callback === 'function') {
    window.zE('webWidget:on', 'chat:departmentStatus', callback);
  }
};

export const onCustomerCareChatDepartmentStatusChange = (
  callback: (status: boolean) => void
): void => {
  if (callback != null && typeof callback === 'function') {
    onZendeskChatDepartmentStatusChange((department: ZendeskChatDepartment) => {
      if (department.name === ZendeskDepartments.CustomerCare) {
        callback(department.status === ZendeskStatus.Online);
      }
    });
  }
};

export const onPCAsChatDepartmentStatusChange = (callback: (status: boolean) => void): void => {
  if (callback != null && typeof callback === 'function') {
    onZendeskChatDepartmentStatusChange((department: ZendeskChatDepartment) => {
      if (department.name === ZendeskDepartments.PrivateClientAdvisors) {
        callback(department.status === ZendeskStatus.Online);
      }
    });
  }
};
